import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { companyQueryKey } from '@/contexts/CompanyContext';
import { onboardingApiClient } from '@/clients/onboardingApiClient';
import { useAuth } from '@/context/AuthContext';
import { userFacingErrorMessage } from '@/utils/userFacingError';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

export function useCompanyOnboarding() {
  const { user, platformRole } = useAuth();
  const queryClient = useQueryClient();
  const [hasCompanyConnection, setHasCompanyConnection] = useState<boolean | null>(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(false);
  const [erpConnectionCompleted, setErpConnectionCompleted] = useState<boolean | null>(false);
  const [bankConnectionCompleted, setBankConnectionCompleted] = useState<boolean | null>(false);
  const [kycCompleted, setKycCompleted] = useState<boolean | null>(false);
  const [companyContextCompleted, setCompanyContextCompleted] = useState<boolean | null>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkOnboardingStatus = useCallback(async () => {
    console.log('Checking onboarding status');
    setIsStatusLoading(true);
    try {
      const result = await onboardingApiClient.getStatus();

      if (result.success) {
        setOnboardingCompleted(result.completed || false);
        setErpConnectionCompleted(result.completed_erp_connection || false);
        setBankConnectionCompleted(result.completed_bank_connection || false);
        setKycCompleted(result.completed_kyc || false);
        setCompanyContextCompleted(result.completed_company_context || false);
      } else {
        // If there's an error getting status, it might mean the company doesn't have onboarding record yet
        setOnboardingCompleted(false);
        setErpConnectionCompleted(false);
        setBankConnectionCompleted(false);
        setKycCompleted(false);
        setCompanyContextCompleted(false);
      }
    } catch (err) {
      console.error('Failed to fetch onboarding status:', err);
      setOnboardingCompleted(false);
      setErpConnectionCompleted(false);
      setBankConnectionCompleted(false);
      setKycCompleted(false);
      setCompanyContextCompleted(false);
    } finally {
      setIsStatusLoading(false);
    }
  }, []);

  const checkCompanyConnection = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Shares CompanyContext's query cache rather than issuing its own request. This
      // callback's identity changes when platformRole resolves from null, so it necessarily
      // runs more than once per mount; going through the cache makes the repeat a cache read
      // instead of another company-api round trip, and concurrent consumers of this hook
      // collapse into one in-flight request (FIN-608).
      const company = await queryClient.fetchQuery({
        queryKey: companyQueryKey(user?.id),
        queryFn: () => ServiceRegistry.getCompanyService().getCompany(),
      });
      const hasConnection = !!company;

      // Platform admins with no active company are in Platform Admin View (see
      // CompanyContext.tsx's isPlatformAdminView) — there's no company to onboard,
      // so treat them as connected/complete. A platform_admin WITH an active company
      // (e.g. one they just created or selected) must go through the same real
      // per-company onboarding check as any other user below.
      if (!hasConnection && platformRole === 'platform_admin') {
        setHasCompanyConnection(true);
        setOnboardingCompleted(true);
        setErpConnectionCompleted(true);
        setBankConnectionCompleted(true);
        setKycCompleted(true);
        setCompanyContextCompleted(true);
        return;
      }

      setHasCompanyConnection(hasConnection);

      // If user has company connection, check onboarding status
      if (hasConnection) {
        console.log('Checking onboarding status');
        await checkOnboardingStatus();
      } else {
        // No company connection means no onboarding completed
        // User should be directed to /join-company first
        setOnboardingCompleted(false);
        setErpConnectionCompleted(false);
        setBankConnectionCompleted(false);
        setKycCompleted(false);
        setCompanyContextCompleted(false);
      }
    } catch (err) {
      setError(userFacingErrorMessage(err, translateOutsideReact('consultantErrors.fallback.unexpected')));
      setHasCompanyConnection(false);
      // Also reset onboarding states on error
      setOnboardingCompleted(false);
      setErpConnectionCompleted(false);
      setBankConnectionCompleted(false);
      setKycCompleted(false);
      setCompanyContextCompleted(false);
    } finally {
      setIsLoading(false);
      setIsInitialLoadComplete(true);
    }
  }, [checkOnboardingStatus, platformRole, queryClient, user?.id]);

  useEffect(() => {
    checkCompanyConnection();
  }, [checkCompanyConnection]);

  // The flags above belong to one company. Switching company in-app does not remount the page
  // (navigating to /overview from /overview is a no-op), so without this the previous company's
  // flags outlive the switch and Overview never opens the setup modal for an un-onboarded one.
  // Same query key and fn as CompanyContext, so this shares its cache rather than adding a request.
  // `undefined` is "not known yet": the first load is already covered by the effect above.
  const { data: activeCompany } = useQuery({
    queryKey: companyQueryKey(user?.id),
    queryFn: () => ServiceRegistry.getCompanyService().getCompany(),
    enabled: !!user?.id,
  });
  const knownCompanyId = activeCompany === undefined ? undefined : (activeCompany?.id ?? null);
  const lastKnownCompanyId = useRef(knownCompanyId);
  useEffect(() => {
    if (knownCompanyId === undefined) return;
    const switched = lastKnownCompanyId.current !== undefined && lastKnownCompanyId.current !== knownCompanyId;
    lastKnownCompanyId.current = knownCompanyId;
    if (switched) void checkCompanyConnection();
  }, [knownCompanyId, checkCompanyConnection]);

  return {
    hasCompanyConnection,
    onboardingCompleted,
    erpConnectionCompleted,
    bankConnectionCompleted,
    kycCompleted,
    isLoading,
    isStatusLoading,
    isInitialLoadComplete,
    error,
    checkCompanyConnection,
    checkOnboardingStatus,
    needsCompanyOnboarding: hasCompanyConnection === false && !isLoading,
    isCompanyOnboardingComplete: onboardingCompleted || false,
    isErpConnectionComplete: erpConnectionCompleted || false,
    isBankConnectionComplete: bankConnectionCompleted || false,
    isKycComplete: kycCompleted || false,
    companyContextCompleted,
    isCompanyContextComplete: companyContextCompleted || false
  };
}
